import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { MoneyInput } from '../../components/MoneyInput';
import { useCustomerList } from '../customers/hooks';
import { useCreateEstimate } from './hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { CreateEstimateLineItem } from './types';

export function NewEstimatePage() {
  usePageTitle('New Estimate');
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselectedCustomerId = params.get('customerId') ?? '';

  const [customerId, setCustomerId] = useState(preselectedCustomerId);
  const [customerFilter, setCustomerFilter] = useState('');
  const [lineItems, setLineItems] = useState<CreateEstimateLineItem[]>([
    { description: '', quantity: 1, unitPrice: 0 }
  ]);
  const [adjustments, setAdjustments] = useState(0);
  const [notes, setNotes] = useState('');

  const create = useCreateEstimate();
  const { data: customers } = useCustomerList({
    search: customerFilter,
    includeInactive: false,
    page: 1,
    pageSize: 25
  });

  const subtotal = useMemo(
    () => lineItems.reduce((s, i) => s + Math.round(i.quantity * i.unitPrice * 100) / 100, 0),
    [lineItems]
  );
  const total = Math.round((subtotal + adjustments) * 100) / 100;

  const canSubmit =
    customerId !== '' &&
    lineItems.length > 0 &&
    lineItems.every((i) => i.description.trim() !== '' && i.unitPrice >= 0 && i.quantity > 0);

  const onSubmit = async () => {
    const created = await create.mutateAsync({
      customerId,
      lineItems: lineItems.map((i) => ({
        description: i.description.trim(),
        quantity: i.quantity,
        unitPrice: i.unitPrice
      })),
      adjustments,
      notes: notes.trim() || null
    });
    nav(`/estimates/${created.id}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to="/estimates" className="text-sm text-slate-500 hover:text-sky-600">
          ← Estimates
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">New Estimate</h1>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Client</span>
          <input
            type="search"
            placeholder="Search clients…"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px] mb-2"
          />
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
          >
            <option value="">Pick a customer…</option>
            {customers?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
                {c.email ? ` (${c.email})` : ''}
              </option>
            ))}
          </select>
        </label>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-slate-700">Line items</h2>
            <button
              type="button"
              onClick={() => setLineItems((li) => [...li, { description: '', quantity: 1, unitPrice: 0 }])}
              className="text-sky-600 hover:text-sky-700 text-sm inline-flex items-center gap-1"
            >
              <Plus size={14} /> Add line
            </button>
          </div>
          <div className="space-y-2">
            {lineItems.map((item, idx) => (
              <div key={idx} className="grid grid-cols-[1fr,72px,120px,40px] gap-2 items-center">
                <input
                  type="text"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    setLineItems((li) => li.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l)))
                  }
                  className="border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={item.quantity}
                  onChange={(e) =>
                    setLineItems((li) => li.map((l, i) => (i === idx ? { ...l, quantity: Number(e.target.value) } : l)))
                  }
                  className="border border-slate-300 rounded px-2 py-2 min-h-[44px]"
                  aria-label="Quantity"
                />
                <MoneyInput
                  value={item.unitPrice}
                  onChange={(v) => setLineItems((li) => li.map((l, i) => (i === idx ? { ...l, unitPrice: v } : l)))}
                  ariaLabel="Unit price"
                />
                <button
                  type="button"
                  onClick={() => setLineItems((li) => li.filter((_, i) => i !== idx))}
                  disabled={lineItems.length === 1}
                  className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                  aria-label="Remove line"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Adjustments</span>
          <input
            type="number"
            step="0.01"
            value={adjustments}
            onChange={(e) => setAdjustments(Number(e.target.value) || 0)}
            className="w-40 border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            aria-label="Adjustments"
          />
          <span className="ml-2 text-xs text-slate-400">Use a negative value for a discount.</span>
        </label>

        <div className="text-right text-sm text-slate-600 space-y-1">
          <div>Subtotal: {subtotal.toFixed(2)}</div>
          {adjustments !== 0 && <div>Adjustments: {adjustments.toFixed(2)}</div>}
          <div className="text-base font-semibold text-slate-800">Total: {total.toFixed(2)}</div>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || create.isPending}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50"
          >
            {create.isPending ? 'Saving…' : 'Save as draft'}
          </button>
          <Link to="/estimates" className="text-slate-600 hover:text-sky-600 text-sm">Cancel</Link>
        </div>

        {create.isError && (
          <p className="text-sm text-red-600">Create failed — check your inputs and try again.</p>
        )}
      </section>
    </div>
  );
}
